import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useCompany } from "@/contexts/CompanyContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Send, Plus, Link2, QrCode, BarChart3, Edit3, Trash2, Sparkles,
  ClipboardList, Copy, ExternalLink, X, GripVertical, Pencil,
} from "lucide-react";
import { toast } from "sonner";
import { PageHero, KpiCard, Section } from "@/components/AnalyticsCommon";

const STATUS_BADGES = {
  rascunho:  "bg-slate-100 text-slate-700 border-slate-200",
  publicada: "bg-emerald-100 text-emerald-700 border-emerald-200",
  encerrada: "bg-rose-100 text-rose-700 border-rose-200",
};

export default function SurveysPage() {
  const navigate = useNavigate();
  const { companyParam, companies, selected, select } = useCompany();
  const [list, setList] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showQr, setShowQr] = useState(null);
  const [editTarget, setEditTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (companyParam) params.company_id = companyParam;
      const [{ data: surveys }, { data: tpls }] = await Promise.all([
        api.get("/surveys", { params }),
        api.get("/surveys/templates"),
      ]);
      setList(surveys);
      setTemplates(tpls);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [companyParam]);

  const launch = async (sid) => {
    try {
      const { data } = await api.post(`/surveys/${sid}/launch`);
      toast.success("Pesquisa publicada! Link e QR Code gerados.");
      setShowQr(data);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const close = async (sid) => {
    if (!window.confirm("Encerrar esta pesquisa?")) return;
    try {
      await api.post(`/surveys/${sid}/close`);
      toast.success("Pesquisa encerrada.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const remove = async (sid) => {
    if (!window.confirm("Excluir definitivamente esta pesquisa e todas as respostas?")) return;
    try {
      await api.delete(`/surveys/${sid}`);
      toast.success("Pesquisa excluída.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-5 pb-10" data-testid="surveys-page">
      <PageHero
        kicker={<><ClipboardList className="inline h-3 w-3 mr-1.5 -mt-0.5" /> Performance & Analytics</>}
        title="Pesquisas & Engajamento"
        description="Crie pesquisas a partir de modelos prontos ou personalizadas. Publique com link e QR Code, acompanhe respostas em tempo real."
        actions={
          <>
            <Select value={selected || "all"} onValueChange={select}>
              <SelectTrigger className="h-10 w-full sm:w-[220px] border-white/20 bg-white/10 text-white" data-testid="surveys-company">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas (Grupo)</SelectItem>
                {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome_fantasia || c.razao_social}</SelectItem>)}
              </SelectContent>
            </Select>
            <NewSurveyDialog templates={templates} onCreated={load} companyId={companyParam} />
          </>
        }
      />

      {/* Templates row */}
      <Section kicker="Modelos prontos" title="Comece a partir de um template" testId="surveys-templates">
        {!companyParam && (
          <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900"
               data-testid="surveys-company-guard">
            <b>Selecione uma empresa</b>
            <span className="text-amber-800/90">
              — para criar ou salvar uma pesquisa, escolha primeiro a empresa à qual ela ficará vinculada.
              "Todas (Grupo)" é uma visão consolidada.
            </span>
            <Button
              size="sm" variant="outline"
              onClick={() => { if (companies?.[0]?.id) select(companies[0].id); }}
              className="ml-auto h-7 border-amber-400 text-amber-900 hover:bg-amber-100"
              data-testid="surveys-select-first-company"
            >
              Selecionar empresa
            </Button>
          </div>
        )}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {templates.map((t) => (
            <TemplateCard key={t.key} tpl={t} onUse={() => instantiateTemplate(t.key, companyParam, load, setEditTarget)} />
          ))}
        </div>
      </Section>

      {/* Surveys list */}
      <Section kicker="Pesquisas" title={`${list.length} pesquisa(s)`} testId="surveys-list">
        {loading ? (
          <div className="grid gap-3">
            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={`sk-${i}`} className="h-[120px]" />)}
          </div>
        ) : list.length === 0 ? (
          <div className="text-center py-10 text-[13px] text-muted-foreground">
            Nenhuma pesquisa cadastrada. Use um <b>modelo</b> ao lado ou clique em <b>Nova pesquisa</b>.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {list.map((s) => (
              <SurveyCard
                key={s.id} survey={s}
                onLaunch={() => launch(s.id)}
                onClose={() => close(s.id)}
                onDelete={() => remove(s.id)}
                onShowQr={() => setShowQr(s)}
                onEdit={() => setEditTarget(s)}
                onResults={() => navigate(`/pesquisas/${s.id}/resultados`)}
              />
            ))}
          </div>
        )}
      </Section>

      {showQr && <QrDialog survey={showQr} onClose={() => setShowQr(null)} />}
      {editTarget && <SurveyEditorDialog survey={editTarget} onClose={() => setEditTarget(null)} onSaved={load} />}
    </div>
  );
}

async function instantiateTemplate(template_key, companyId, reload, openEditor) {
  if (!companyId) {
    toast.error("Selecione uma empresa antes de criar a pesquisa.", {
      description: "No seletor acima, escolha a empresa à qual a pesquisa ficará vinculada. \"Todas (Grupo)\" não vincula a nenhuma.",
      duration: 6000,
    });
    return;
  }
  try {
    const { data } = await api.post("/surveys/from-template", { template_key, company_id: companyId });
    toast.success("Modelo carregado. Revise as perguntas e publique.");
    // Reload list AND open editor with the freshly-created survey so the user
    // can review/edit the questions before publishing.
    reload();
    if (openEditor && data && data.id) openEditor(data);
  } catch (e) {
    toast.error(formatApiError(e.response?.data?.detail));
  }
}

function TemplateCard({ tpl, onUse }) {
  const colors = {
    clima: "from-emerald-500/10 to-emerald-500/5 border-emerald-200",
    engajamento: "from-[#C5A059]/15 to-[#FAF4E5] border-[#C5A059]/30",
    onboarding: "from-sky-500/10 to-sky-500/5 border-sky-200",
    desligamento: "from-rose-500/10 to-rose-500/5 border-rose-200",
  };
  return (
    <button onClick={onUse}
            className={`text-left rounded-xl border bg-gradient-to-br p-4 hover:shadow-md transition-all hover:-translate-y-0.5 ${colors[tpl.category] || ""}`}
            data-testid={`template-${tpl.key}`}>
      <Sparkles className="h-4 w-4 text-[#8A6B2B] mb-2" />
      <div className="font-display text-[14px] font-semibold leading-tight">{tpl.title}</div>
      <p className="text-[11px] text-muted-foreground mt-1 line-clamp-2">{tpl.description}</p>
      <div className="mt-2 text-[10px] text-[#8A6B2B] font-medium">{tpl.questions_count} perguntas</div>
    </button>
  );
}

function SurveyCard({ survey, onLaunch, onClose, onDelete, onShowQr, onEdit, onResults }) {
  const editable = survey.status === "rascunho";
  return (
    <div className="rounded-xl border border-border bg-white p-4" data-testid={`survey-${survey.id}`}>
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="min-w-0 flex-1">
          <div className="font-display text-[15px] font-semibold truncate">{survey.title}</div>
          <div className="text-[11px] text-muted-foreground truncate">{survey.description}</div>
        </div>
        <Badge variant="outline" className={`text-[10px] ${STATUS_BADGES[survey.status]}`}>
          {survey.status}
        </Badge>
      </div>
      <div className="grid grid-cols-3 gap-2 my-3 text-center">
        <Mini label="Perguntas" value={survey.questions?.length || 0} />
        <Mini label="Respostas" value={survey.responses_count || 0} />
        <Mini label="Modo" value={survey.anonymous ? "Anônima" : "Identif."} />
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        {editable && (
          <Button size="sm" variant="outline" onClick={onEdit}
                  data-testid={`edit-${survey.id}`}>
            <Pencil className="h-3 w-3 mr-1" /> Editar
          </Button>
        )}
        {editable && (
          <Button size="sm" onClick={onLaunch}
                  className="bg-[#234935] hover:bg-[#2c5c43] text-white"
                  data-testid={`launch-${survey.id}`}>
            <Send className="h-3 w-3 mr-1" /> Publicar
          </Button>
        )}
        {survey.status === "publicada" && (
          <>
            <Button size="sm" variant="outline" onClick={onShowQr} data-testid={`qr-${survey.id}`}>
              <QrCode className="h-3 w-3 mr-1" /> Link & QR
            </Button>
            <Button size="sm" variant="outline" onClick={onClose}>Encerrar</Button>
          </>
        )}
        {(survey.responses_count > 0 || survey.status === "encerrada") && (
          <Button size="sm" variant="outline" onClick={onResults} data-testid={`results-${survey.id}`}>
            <BarChart3 className="h-3 w-3 mr-1" /> Resultados
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={onDelete}
                className="text-rose-600 hover:bg-rose-50 ml-auto"
                data-testid={`del-${survey.id}`}>
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div className="rounded-md border border-border bg-slate-50/40 py-1.5">
      <div className="text-[9.5px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-display text-[14px] font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function QrDialog({ survey, onClose }) {
  const [s, setS] = useState(survey);
  useEffect(() => {
    if (!survey.qr_data_url) {
      api.get(`/surveys/${survey.id}`).then(({ data }) => setS(data));
    }
  }, [survey]);
  const copy = () => {
    navigator.clipboard.writeText(s.public_url);
    toast.success("Link copiado!");
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[460px]" aria-describedby="qr-desc" data-testid="qr-dialog">
        <DialogHeader>
          <DialogTitle>{s.title}</DialogTitle>
        </DialogHeader>
        <p id="qr-desc" className="text-[12px] text-muted-foreground">
          Compartilhe o link ou QR Code com os colaboradores.
        </p>
        {s.qr_data_url && (
          <div className="flex justify-center my-4">
            <img src={s.qr_data_url} alt="QR Code" className="rounded-lg border border-border w-[260px] h-[260px]" data-testid="qr-image" />
          </div>
        )}
        {s.public_url && (
          <div className="rounded-md border border-border bg-slate-50 p-2.5 flex items-center gap-2 text-[11.5px] font-mono">
            <Link2 className="h-3.5 w-3.5 text-[#C5A059] shrink-0" />
            <span className="truncate flex-1" data-testid="public-link">{s.public_url}</span>
            <Button size="sm" variant="ghost" onClick={copy} className="h-7"><Copy className="h-3 w-3" /></Button>
            <a href={s.public_url} target="_blank" rel="noopener noreferrer">
              <Button size="sm" variant="ghost" className="h-7"><ExternalLink className="h-3 w-3" /></Button>
            </a>
          </div>
        )}
        <DialogFooter>
          <Button onClick={onClose} variant="outline">Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewSurveyDialog({ templates, onCreated, companyId }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const create = async () => {
    if (!companyId) {
      toast.error("Selecione uma empresa antes de criar a pesquisa.", {
        description: "No seletor acima, escolha a empresa à qual a pesquisa ficará vinculada. \"Todas (Grupo)\" não vincula a nenhuma.",
        duration: 6000,
      });
      return;
    }
    if (!title) { toast.error("Informe o título"); return; }
    setSaving(true);
    try {
      await api.post("/surveys", {
        title, description: "",
        company_id: companyId,
        anonymous: true, prevent_duplicate: true,
        questions: [
          { key: "q1", type: "scale_5", label: "Pergunta 1", required: true, order: 0 },
        ],
      });
      toast.success("Pesquisa criada como rascunho. Edite e adicione perguntas.");
      setOpen(false);
      setTitle("");
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="h-10 bg-[#C5A059] hover:bg-[#D4B36B] text-[#1A231F]"
                data-testid="new-survey-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> Nova pesquisa
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[460px]" aria-describedby="ns-desc">
        <DialogHeader>
          <DialogTitle>Nova pesquisa em branco</DialogTitle>
        </DialogHeader>
        <p id="ns-desc" className="text-[12px] text-muted-foreground">
          Para começar mais rápido, considere usar um <b>modelo pronto</b>.
        </p>
        <div className="my-2">
          <input value={title} onChange={(e) => setTitle(e.target.value)}
                 placeholder="Título da pesquisa"
                 className="w-full h-10 px-3 border border-border rounded-md text-sm"
                 data-testid="ns-title" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={create} disabled={saving}
                  className="bg-[#C5A059] hover:bg-[#D4B36B] text-[#1A231F]"
                  data-testid="ns-save">
            Criar rascunho
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// SurveyEditorDialog — editor completo de pesquisa em rascunho
// ---------------------------------------------------------------------------
const QUESTION_TYPES = [
  { value: "scale_5",  label: "Escala 1 a 5" },
  { value: "scale_10", label: "Escala 1 a 10" },
  { value: "nps",      label: "NPS (0 a 10)" },
  { value: "stars",    label: "Estrelas (1 a 5)" },
  { value: "yesno",    label: "Sim / Não" },
  { value: "single",   label: "Escolha única (opções)" },
  { value: "multi",    label: "Múltipla escolha (opções)" },
  { value: "open",     label: "Resposta aberta (texto)" },
];

function SurveyEditorDialog({ survey, onClose, onSaved }) {
  const [title, setTitle] = useState(survey.title || "");
  const [description, setDescription] = useState(survey.description || "");
  const [anonymous, setAnonymous] = useState(!!survey.anonymous);
  const [preventDup, setPreventDup] = useState(!!survey.prevent_duplicate);
  const [questions, setQuestions] = useState(
    (survey.questions || []).map((q, i) => ({
      key: q.key || `q${i + 1}`,
      type: q.type || "scale_5",
      label: q.label || "",
      required: !!q.required,
      order: q.order ?? i,
      options: q.options || [],
    }))
  );
  const [saving, setSaving] = useState(false);

  const addQuestion = () => {
    setQuestions((prev) => [
      ...prev,
      {
        key: `q${prev.length + 1}`,
        type: "scale_5",
        label: "",
        required: false,
        order: prev.length,
        options: [],
      },
    ]);
  };

  const updateQuestion = (idx, patch) => {
    setQuestions((prev) => prev.map((q, i) => (i === idx ? { ...q, ...patch } : q)));
  };

  const removeQuestion = (idx) => {
    setQuestions((prev) => prev.filter((_, i) => i !== idx).map((q, i) => ({ ...q, order: i })));
  };

  const moveQuestion = (idx, dir) => {
    setQuestions((prev) => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next.map((q, i) => ({ ...q, order: i }));
    });
  };

  const updateOption = (qIdx, optIdx, value) => {
    setQuestions((prev) =>
      prev.map((q, i) => {
        if (i !== qIdx) return q;
        const opts = [...(q.options || [])];
        opts[optIdx] = value;
        return { ...q, options: opts };
      })
    );
  };
  const addOption = (qIdx) => {
    setQuestions((prev) =>
      prev.map((q, i) => (i === qIdx ? { ...q, options: [...(q.options || []), ""] } : q))
    );
  };
  const removeOption = (qIdx, optIdx) => {
    setQuestions((prev) =>
      prev.map((q, i) =>
        i === qIdx ? { ...q, options: (q.options || []).filter((_, k) => k !== optIdx) } : q
      )
    );
  };

  const validate = () => {
    if (!title.trim()) return "Informe o título da pesquisa.";
    if (questions.length === 0) return "Adicione pelo menos uma pergunta.";
    for (const [i, q] of questions.entries()) {
      if (!q.label.trim()) return `Pergunta ${i + 1}: informe o enunciado.`;
      if (["single", "multi"].includes(q.type)) {
        const opts = (q.options || []).filter((o) => (o || "").trim());
        if (opts.length < 2) {
          return `Pergunta ${i + 1}: informe pelo menos 2 opções.`;
        }
      }
    }
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim() || null,
        category: survey.category || null,
        company_id: survey.company_id || null,
        anonymous,
        prevent_duplicate: preventDup,
        target_sectors: survey.target_sectors || null,
        target_cargos: survey.target_cargos || null,
        target_turnos: survey.target_turnos || null,
        date_start: survey.date_start || null,
        date_end: survey.date_end || null,
        questions: questions.map((q, i) => ({
          key: q.key || `q${i + 1}`,
          type: q.type,
          label: q.label.trim(),
          required: !!q.required,
          order: i,
          options: ["single", "multi"].includes(q.type)
            ? (q.options || []).map((o) => (o || "").trim()).filter(Boolean)
            : null,
        })),
      };
      await api.put(`/surveys/${survey.id}`, payload);
      toast.success("Pesquisa salva.");
      onClose();
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[720px] max-h-[85vh] overflow-y-auto" data-testid="survey-editor">
        <DialogHeader>
          <DialogTitle>Editar pesquisa</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-[11px] uppercase tracking-wider">Título *</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)}
                   placeholder="Ex: Pesquisa de Clima 2026"
                   data-testid="editor-title" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider">Descrição</Label>
            <Textarea rows={2} value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Contexto da pesquisa (opcional)"
                      data-testid="editor-desc" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center gap-2 text-[12px]">
              <Switch checked={anonymous} onCheckedChange={setAnonymous}
                      data-testid="editor-anon" />
              Anônima (não identifica respondente)
            </label>
            <label className="flex items-center gap-2 text-[12px]">
              <Switch checked={preventDup} onCheckedChange={setPreventDup}
                      data-testid="editor-nodup" />
              Impedir respostas duplicadas (mesma matrícula)
            </label>
          </div>

          <div className="border-t border-slate-200 pt-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-[0.15em] font-medium text-slate-600">
                Perguntas ({questions.length})
              </div>
              <Button size="sm" variant="outline" onClick={addQuestion}
                      data-testid="editor-add-question">
                <Plus className="h-3 w-3 mr-1" /> Adicionar pergunta
              </Button>
            </div>

            <div className="space-y-3">
              {questions.map((q, idx) => (
                <div key={q.key || `q-${idx}`} className="rounded-lg border border-slate-200 bg-slate-50/40 p-3"
                     data-testid={`editor-q-${idx}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[11px] font-semibold text-slate-500 w-6">#{idx + 1}</span>
                    <Select value={q.type} onValueChange={(v) => updateQuestion(idx, { type: v })}>
                      <SelectTrigger className="h-9 flex-1" data-testid={`editor-q-type-${idx}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {QUESTION_TYPES.map((t) => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <label className="flex items-center gap-1 text-[11px] px-2">
                      <input type="checkbox" checked={q.required}
                             onChange={(e) => updateQuestion(idx, { required: e.target.checked })}
                             data-testid={`editor-q-req-${idx}`} />
                      Obrigatória
                    </label>
                    <Button size="sm" variant="ghost" onClick={() => moveQuestion(idx, -1)}
                            disabled={idx === 0} className="h-8 w-8 p-0">↑</Button>
                    <Button size="sm" variant="ghost" onClick={() => moveQuestion(idx, 1)}
                            disabled={idx === questions.length - 1} className="h-8 w-8 p-0">↓</Button>
                    <Button size="sm" variant="ghost" onClick={() => removeQuestion(idx)}
                            className="text-rose-600 hover:bg-rose-50 h-8 w-8 p-0"
                            data-testid={`editor-q-del-${idx}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  <Input value={q.label} onChange={(e) => updateQuestion(idx, { label: e.target.value })}
                         placeholder="Enunciado da pergunta"
                         data-testid={`editor-q-label-${idx}`} />
                  {["single", "multi"].includes(q.type) && (
                    <div className="mt-2 space-y-1.5">
                      <Label className="text-[10.5px] uppercase tracking-wider text-slate-500">Opções</Label>
                      {(q.options || []).map((opt, oi) => (
                        <div key={oi} className="flex items-center gap-2">
                          <Input value={opt} onChange={(e) => updateOption(idx, oi, e.target.value)}
                                 placeholder={`Opção ${oi + 1}`}
                                 className="h-8 text-[12.5px]"
                                 data-testid={`editor-q-opt-${idx}-${oi}`} />
                          <Button size="sm" variant="ghost" onClick={() => removeOption(idx, oi)}
                                  className="text-rose-600 hover:bg-rose-50 h-8 w-8 p-0">
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                      <Button size="sm" variant="outline" onClick={() => addOption(idx)}
                              className="mt-1 h-8 text-[11px]"
                              data-testid={`editor-q-addopt-${idx}`}>
                        <Plus className="h-3 w-3 mr-1" /> Adicionar opção
                      </Button>
                    </div>
                  )}
                </div>
              ))}
              {questions.length === 0 && (
                <div className="text-center py-6 text-[12.5px] text-muted-foreground border border-dashed border-slate-300 rounded-md">
                  Nenhuma pergunta ainda — clique em <b>Adicionar pergunta</b>.
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={saving}
                  className="bg-[#234935] hover:bg-[#2c5c43] text-white"
                  data-testid="editor-save">
            {saving ? "Salvando…" : "Salvar pesquisa"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
